import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { Loader2, X, ImagePlus, Check, Pencil, Save, Trash2, Clock, Search, ExternalLink, Hash, Camera, Palette, Sparkles, Wand2, Copy } from 'lucide-react';
import { generatePosts } from '../services/theSeer';
import ImageGenerator from '../components/HermodsPath/ImageGenerator';
import LuxAssetBrowser from '../components/HermodsPath/LuxAssetBrowser';
import { resolveAssetFromNote } from '../services/luxLibrisAssets';
import ImagePromptModal from '../components/HermodsPath/ImagePromptModal';
import InspirationPanel from '../components/HermodsPath/InspirationPanel';
import { buildBasePrompt, enhancePrompt } from '../services/imagePromptGenerator';
import {
  saveDraft, getDrafts, deleteDraft,
  logCopiedPost, getPostHistory, updateDraftStatus
} from '../services/hermodsPathService';

const CONTENT_TYPES = [
  'Mini-Museum',
  'Everything-Arium',
  'Bird Window Update',
  'Saturday Opening',
  'Lux Libris',
  'Publishing House',
  'Mini-Modern',
  'MS Research Center',
  'Professional Development',
  'General'
];

const PLATFORMS = [
  { id: 'professional-ig', label: 'Professional IG', color: '#FFD93D', activeText: 'text-gray-800' },
  { id: 'lux-libris-ig', label: 'Lux Libris IG', color: '#ADD4EA', activeText: 'text-gray-800' },
  { id: 'linkedin', label: 'LinkedIn', color: '#4389BD', activeText: 'text-white' }
];

const PLATFORM_HASHTAGS = {
  'professional-ig': [
    '#LibraryInnovation', '#SchoolLibrary', '#LibrarianLife', '#CatholicEducation',
    '#EdTech', '#LibrariesOfInstagram', '#SchoolLibrarian', '#LibraryProgram',
    '#STEMLibrary', '#LibraryTransformation', '#ReadingCulture', '#LibraryLove'
  ],
  'lux-libris-ig': [
    '#CatholicReading', '#CatholicBooks', '#CatholicEducation', '#FaithFormation',
    '#CatholicSchool', '#SaintOfTheDay', '#CatholicKids', '#CatholicHomeschool',
    '#LuxLibris', '#CatholicLiterature', '#FormingSaints'
  ],
  'linkedin': [
    '#LibraryInnovation', '#EdTech', '#CatholicEducation', '#SchoolLibraries',
    '#LibrarianLife', '#EducationLeadership', '#LibraryTransformation'
  ],
  'professional-fb': [
    '#LibraryInnovation', '#SchoolLibrary', '#LibrarianLife', '#CatholicEducation', '#EdTech'
  ],
  'lux-libris-fb': [
    '#CatholicReading', '#CatholicBooks', '#CatholicEducation', '#LuxLibris', '#FaithFormation'
  ],
};

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
  return Promise.resolve();
}

const getPostText = (post) => typeof post === 'string' ? post : post?.text || '';

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-6 right-6 ${type === 'error' ? 'bg-red-600' : 'bg-green-600'} text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2 z-50 animate-bounce`}>
      {type === 'error' ? <X size={16} /> : <Check size={16} />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-1 hover:opacity-80"><X size={14} /></button>
    </div>
  );
}

function PreviewCard({ icon, label, handle, color, content, charLimit, copyLabel, previewImage, isWide, onContentChange, onCopy, platformId, imageNote, imageType, onGoToTemplates, resolvedAsset, onGenerateImagePrompt }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(content);
  const textareaRef = useRef(null);

  const platformLink = platformId === 'linkedin'
    ? { url: 'https://www.linkedin.com/feed/', label: 'Open LinkedIn' }
    : platformId === 'professional-fb' || platformId === 'lux-libris-fb'
      ? { url: 'https://www.facebook.com/', label: 'Open Facebook' }
      : { url: 'https://www.instagram.com/', label: 'Open Instagram' };

  const handleCopy = async () => {
    await copyToClipboard(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
    if (onCopy) onCopy(platformId);
  };

  const startEditing = () => {
    setEditText(content);
    setEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }, 0);
  };

  const saveEdit = () => {
    onContentChange(editText);
    setEditing(false);
  };

  const charPct = content.length / charLimit;
  const isOverLimit = charPct > 1;
  const isWarning = charPct >= 0.9 && charPct <= 1;

  // For LinkedIn: extract first two lines for "above the fold"
  const linkedInFold = isWide ? content.split('\n').slice(0, 2).join('\n') : null;

  return (
    <div className={`bg-slate-800 rounded-2xl shadow-xl overflow-hidden border border-slate-700 ${isWide ? '' : 'max-w-sm mx-auto'}`}>
      {/* Colour accent top border */}
      <div className="h-1.5" style={{ backgroundColor: color }} />

      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <div>
            <p className="text-white text-sm font-bold">{label}</p>
            <p className="text-slate-400 text-xs">{handle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={editing ? saveEdit : startEditing}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition bg-slate-700 text-slate-200 hover:bg-slate-600 flex items-center gap-1"
          >
            {editing ? (
              <><Check size={12} /> Save</>
            ) : (
              <><Pencil size={12} /> Edit</>
            )}
          </button>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1"
            style={copied ? { backgroundColor: color, color: '#1e293b' } : { backgroundColor: 'rgba(255,255,255,0.1)', color: '#e2e8f0' }}
          >
            {copied ? (
              <><Check size={12} /> Copied!</>
            ) : (
              copyLabel
            )}
          </button>
          {copied && (
            <a
              href={platformLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 bg-slate-600 text-slate-100 hover:bg-slate-500"
            >
              <ExternalLink size={12} />
              {platformLink.label}
            </a>
          )}
        </div>
      </div>

      {/* Image preview for IG cards */}
      {previewImage && !isWide && (
        <div className="aspect-square bg-slate-900">
          <img
            src={previewImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content area */}
      <div className="p-4">
        {/* LinkedIn above-the-fold highlight */}
        {isWide && !editing && linkedInFold && (
          <div className="mb-3 pb-3 border-b border-slate-700">
            <p className="text-slate-200 text-sm font-semibold whitespace-pre-wrap leading-relaxed">{linkedInFold}</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-slate-500 text-xs">above the fold</p>
              <p className={`text-xs font-mono ${linkedInFold.length > 210 ? 'text-yellow-400' : 'text-slate-500'}`}>
                {linkedInFold.length}/210
              </p>
            </div>
          </div>
        )}

        {editing ? (
          <textarea
            ref={textareaRef}
            value={editText}
            onChange={(e) => {
              setEditText(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-3 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none leading-relaxed"
            style={{ minHeight: '200px' }}
          />
        ) : (
          <p className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">{content}</p>
        )}

        {/* Image recommendation */}
        {imageType && imageType !== 'none' && imageNote && (
          <div className="mt-3 bg-slate-700/50 rounded-lg p-3">
            <div className="flex items-start gap-2">
              {imageType === 'photo' && <Camera size={14} className="text-amber-400 mt-0.5 shrink-0" />}
              {imageType === 'graphic' && <Palette size={14} className="text-rose-400 mt-0.5 shrink-0" />}
              {imageType === 'lux_asset' && <Sparkles size={14} className="text-sky-300 mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-300 leading-relaxed">{imageNote}</p>
                {imageType === 'photo' && (
                  <p className="text-xs text-slate-500 mt-1">Upload a photo above</p>
                )}
                {imageType === 'graphic' && (
                  <div className="flex items-center gap-3 mt-1">
                    {onGenerateImagePrompt && (
                      <button
                        onClick={() => onGenerateImagePrompt(platformId, imageNote)}
                        className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 transition"
                      >
                        <Wand2 size={10} /> Generate Image Prompt
                      </button>
                    )}
                    {onGoToTemplates && (
                      <button
                        onClick={onGoToTemplates}
                        className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1 transition"
                      >
                        <Palette size={10} /> Create in Templates
                      </button>
                    )}
                  </div>
                )}
                {imageType === 'lux_asset' && (
                  resolvedAsset?.url ? (
                    <div className="mt-2 flex items-end gap-3">
                      <img
                        src={resolvedAsset.url}
                        alt={resolvedAsset.name}
                        className="w-16 h-16 rounded-lg object-cover border border-slate-600"
                      />
                      {onGoToTemplates && (
                        <button
                          onClick={() => onGoToTemplates(resolvedAsset.url)}
                          className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1 transition"
                        >
                          <Palette size={10} /> Use in graphic
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-sky-400/70 mt-1">Lux Libris asset</p>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* Character count */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
          <p className={`text-xs font-mono ${isOverLimit ? 'text-red-400 font-bold' : isWarning ? 'text-yellow-400 font-semibold' : 'text-slate-500'}`}>
            {content.length} / {charLimit.toLocaleString()}
          </p>
          {isOverLimit && (
            <p className="text-xs text-red-400">{content.length - charLimit} over limit</p>
          )}
          {isWarning && (
            <p className="text-xs text-yellow-400">Approaching limit</p>
          )}
        </div>
      </div>

      {/* Hashtag suggestions */}
      {PLATFORM_HASHTAGS[platformId] && (
        <div className="px-4 pb-4">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Hash size={10} /> Suggested hashtags
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PLATFORM_HASHTAGS[platformId].map(tag => {
              const isActive = content.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() => {
                    if (isActive) {
                      const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                      const regex = new RegExp(`\\s*${escaped}(?=\\s|$)`, 'g');
                      onContentChange(content.replace(regex, '').trim());
                    } else {
                      const separator = content.endsWith('\n') || content.endsWith(' ') ? '' : '\n\n';
                      onContentChange(content + separator + tag);
                    }
                  }}
                  className={`text-xs px-2 py-1 rounded-full transition-all ${
                    isActive
                      ? 'text-white shadow-sm'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                  }`}
                  style={isActive ? { backgroundColor: color } : {}}
                >
                  {tag}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StandalonePromptGenerator() {
  const [description, setDescription] = useState('');
  const [platform, setPlatform] = useState('professional-ig');
  const [tool, setTool] = useState(() => localStorage.getItem('hermodsPath_preferredImageTool') || 'canva');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!description.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await enhancePrompt(description, tool, platform);
      setPrompt(result);
    } catch (err) {
      setError(err.message);
      setPrompt(buildBasePrompt(description, tool, platform));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    await copyToClipboard(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="space-y-3">
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe the image you want to create..."
        className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-3 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
        rows={3}
      />
      <div className="flex gap-2">
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="flex-1 bg-slate-900 text-slate-200 text-sm rounded-lg p-2 border border-slate-600"
        >
          {PLATFORMS.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <select
          value={tool}
          onChange={(e) => { setTool(e.target.value); localStorage.setItem('hermodsPath_preferredImageTool', e.target.value); }}
          className="flex-1 bg-slate-900 text-slate-200 text-sm rounded-lg p-2 border border-slate-600"
        >
          <option value="canva">Canva Magic Media</option>
          <option value="leonardo">Leonardo AI</option>
        </select>
      </div>
      <button
        onClick={handleGenerate}
        disabled={!description.trim() || generating}
        className="w-full py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5 bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {generating ? <><Loader2 size={14} className="animate-spin" /> Generating...</> : <><Wand2 size={14} /> Generate Prompt</>}
      </button>
      {error && <p className="text-xs text-red-400">{error}</p>}
      {prompt && (
        <div className="space-y-2">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-3 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none leading-relaxed"
            rows={5}
          />
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition flex items-center justify-center gap-1 ${copied ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
            >
              {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy Prompt</>}
            </button>
            <a
              href={tool === 'canva' ? 'https://www.canva.com/magic-media/' : 'https://app.leonardo.ai/ai-generations'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2 rounded-lg text-xs font-medium transition flex items-center justify-center gap-1 bg-slate-600 text-slate-200 hover:bg-slate-500"
            >
              <ExternalLink size={12} /> Open {tool === 'canva' ? 'Canva' : 'Leonardo'}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function HermodsPath() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const fileInputRef = useRef(null);

  // Compose state
  const [rawText, setRawText] = useState('');
  const [images, setImages] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['professional-ig', 'lux-libris-ig', 'linkedin']);
  const [contentType, setContentType] = useState('General');
  const [includeProfessionalFB, setIncludeProfessionalFB] = useState(false);
  const [includeLuxLibrisFB, setIncludeLuxLibrisFB] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedPosts, setGeneratedPosts] = useState(null);
  const [error, setError] = useState(null);

  // Navigation
  const [activeTab, setActiveTab] = useState('compose');

  // Persistence state
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('');
  const [historySearch, setHistorySearch] = useState('');

  // Drag state
  const [dragging, setDragging] = useState(false);

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // Image prompt modal
  const [imagePromptModal, setImagePromptModal] = useState({
    isOpen: false, imageNote: '', platformId: '', contentType: '',
  });

  // Load drafts and history on mount
  useEffect(() => {
    if (!currentUser) return;
    loadDrafts();
    loadHistory();
  }, [currentUser]);

  // Track unsaved changes
  useEffect(() => {
    if (rawText || generatedPosts) {
      setHasUnsavedChanges(true);
    }
  }, [rawText, contentType, selectedPlatforms, generatedPosts]);

  // Auto-save every 60 seconds
  useEffect(() => {
    if (!currentUser || !hasUnsavedChanges || !rawText.trim()) return;

    const timer = setTimeout(() => {
      handleSaveDraft(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, [currentUser, hasUnsavedChanges, rawText, contentType, selectedPlatforms, generatedPosts]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl + Enter: Generate content
      if (isMod && e.key === 'Enter') {
        e.preventDefault();
        if (rawText.trim().length > 0 && selectedPlatforms.length > 0 && !generating) {
          handleGenerate();
        }
        return;
      }

      // Cmd/Ctrl + Shift + C: Copy all generated content
      if (isMod && e.shiftKey && (e.key === 'c' || e.key === 'C')) {
        e.preventDefault();
        handleCopyAll();
        return;
      }

      // Escape: Close modals/editors or clear error
      if (e.key === 'Escape') {
        if (deleteConfirm) setDeleteConfirm(null);
        else if (error) setError(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  const loadDrafts = async () => {
    if (!currentUser) return;
    try {
      const data = await getDrafts(currentUser.uid);
      setDrafts(data);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    }
  };

  const loadHistory = async () => {
    if (!currentUser) return;
    try {
      const filters = {};
      if (historyFilter) filters.contentType = historyFilter;
      const data = await getPostHistory(currentUser.uid, filters);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  // Reload history when filter changes
  useEffect(() => {
    loadHistory();
  }, [historyFilter]);

  const handleSaveDraft = async (isAutoSave = false) => {
    if (!currentUser || !rawText.trim()) return;
    setSaving(true);
    try {
      const draftData = {
        rawInput: rawText,
        contentType,
        platforms: selectedPlatforms,
        includeFacebook: { professional: includeProfessionalFB, luxLibris: includeLuxLibrisFB },
        generatedDrafts: generatedPosts || { professionalIG: null, luxLibrisIG: null, linkedin: null, professionalFB: null, luxLibrisFB: null },
        editedDrafts: generatedPosts || { professionalIG: null, luxLibrisIG: null, linkedin: null, professionalFB: null, luxLibrisFB: null },
        images: [],
        status: 'draft',
      };
      const id = await saveDraft(currentUser.uid, draftData, currentDraftId);
      setCurrentDraftId(id);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      if (!isAutoSave) loadDrafts();
    } catch (err) {
      console.error('Failed to save draft:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDraft = (draft) => {
    setRawText(draft.rawInput || '');
    setContentType(draft.contentType || 'General');
    setSelectedPlatforms(draft.platforms?.length ? draft.platforms : ['professional-ig', 'lux-libris-ig', 'linkedin']);
    setIncludeProfessionalFB(draft.includeFacebook?.professional || false);
    setIncludeLuxLibrisFB(draft.includeFacebook?.luxLibris || false);
    const edited = draft.editedDrafts;
    const generated = draft.generatedDrafts;
    setGeneratedPosts(
      edited?.professionalIG || edited?.luxLibrisIG || edited?.linkedin || edited?.professionalFB || edited?.luxLibrisFB
        ? edited
        : generated?.professionalIG || generated?.luxLibrisIG || generated?.linkedin || generated?.professionalFB || generated?.luxLibrisFB
          ? generated
          : null
    );
    setCurrentDraftId(draft.id);
    setHasUnsavedChanges(false);
    setActiveTab('compose');
  };

  const handleDeleteDraft = async (draftId) => {
    if (!currentUser) return;
    try {
      await deleteDraft(currentUser.uid, draftId);
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
      }
      setDeleteConfirm(null);
      loadDrafts();
    } catch (err) {
      console.error('Failed to delete draft:', err);
    }
  };

  const handleCopyLog = async (platformId) => {
    if (!currentUser || !generatedPosts) return;
    try {
      await logCopiedPost(currentUser.uid, {
        content: {
          professionalIG: getPostText(generatedPosts.professionalIG) || null,
          luxLibrisIG: getPostText(generatedPosts.luxLibrisIG) || null,
          linkedin: getPostText(generatedPosts.linkedin) || null,
          professionalFB: getPostText(generatedPosts.professionalFB) || null,
          luxLibrisFB: getPostText(generatedPosts.luxLibrisFB) || null,
        },
        contentType,
        platforms: selectedPlatforms,
      });
      if (currentDraftId) {
        await updateDraftStatus(currentUser.uid, currentDraftId, 'published');
      }
      loadHistory();
      const FB_LABELS = { 'professional-fb': 'Professional FB', 'lux-libris-fb': 'Lux Libris FB' };
      const platformLabel = PLATFORMS.find(p => p.id === platformId)?.label || FB_LABELS[platformId] || 'Post';
      showToast(`${platformLabel} copied to clipboard!`);
    } catch (err) {
      console.error('Failed to log copy:', err);
      showToast('Copied, but failed to save to history', 'error');
    }
  };

  const handleCopyAll = async () => {
    if (!generatedPosts) return;
    const parts = [];
    if (generatedPosts.professionalIG) parts.push(`--- Professional IG ---\n${getPostText(generatedPosts.professionalIG)}`);
    if (generatedPosts.professionalFB) parts.push(`--- Professional FB ---\n${getPostText(generatedPosts.professionalFB)}`);
    if (generatedPosts.luxLibrisIG) parts.push(`--- Lux Libris IG ---\n${getPostText(generatedPosts.luxLibrisIG)}`);
    if (generatedPosts.luxLibrisFB) parts.push(`--- Lux Libris FB ---\n${getPostText(generatedPosts.luxLibrisFB)}`);
    if (generatedPosts.linkedin) parts.push(`--- LinkedIn ---\n${getPostText(generatedPosts.linkedin)}`);
    if (parts.length === 0) return;
    await copyToClipboard(parts.join('\n\n'));
    showToast('All posts copied to clipboard!');
  };

  const handleNewDraft = () => {
    setRawText('');
    setImages([]);
    setSelectedPlatforms(['professional-ig', 'lux-libris-ig', 'linkedin']);
    setContentType('General');
    setIncludeProfessionalFB(false);
    setIncludeLuxLibrisFB(false);
    setGeneratedPosts(null);
    setCurrentDraftId(null);
    setHasUnsavedChanges(false);
    setError(null);
    setActiveTab('compose');
  };

  const handleTextChange = (e) => {
    setRawText(e.target.value);
    // Auto-expand textarea
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const addImages = useCallback((files) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const newImages = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) continue;
      if (images.length + newImages.length >= 10) break;
      newImages.push({
        id: Date.now() + Math.random(),
        file,
        preview: URL.createObjectURL(file)
      });
    }

    setImages(prev => [...prev, ...newImages].slice(0, 10));
  }, [images.length]);

  const removeImage = (id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img && img.preview?.startsWith('blob:')) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== id);
    });
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) {
      addImages(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) {
      addImages(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => {
      // Don't allow deselecting the last platform
      if (prev.includes(platformId) && prev.length === 1) return prev;
      return prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId];
    });
  };

  const handleAddGeneratedImage = useCallback((file) => {
    if (images.length >= 10) return;
    const img = {
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
    };
    setImages(prev => [...prev, img].slice(0, 10));
  }, [images.length]);

  const handleGenerateImagePrompt = (platformId, imageNote) => {
    setImagePromptModal({ isOpen: true, imageNote, platformId, contentType });
  };

  const closeImagePromptModal = () => {
    setImagePromptModal(prev => ({ ...prev, isOpen: false }));
  };

  const canGenerate = rawText.trim().length > 0 && selectedPlatforms.length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);

    try {
      // Build platforms list including FB when checkboxes are active
      const allPlatforms = [...selectedPlatforms];
      if (includeProfessionalFB && selectedPlatforms.includes('professional-ig')) {
        allPlatforms.push('professional-fb');
      }
      if (includeLuxLibrisFB && selectedPlatforms.includes('lux-libris-ig')) {
        allPlatforms.push('lux-libris-fb');
      }

      const posts = await generatePosts({
        rawText,
        contentType,
        platforms: allPlatforms,
        imageDescriptions: images.map((img) => img.file.name),
      });
      setGeneratedPosts(posts);
    } catch (err) {
      console.error('Generation failed:', err);
      setError(err.message || 'The Seer encountered an error. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const TABS = [
    { id: 'compose', label: 'Compose', icon: '✏️' },
    { id: 'drafts', label: 'Drafts', icon: '📄', count: drafts.length },
    { id: 'history', label: 'History', icon: '📋', count: history.length },
    { id: 'templates', label: 'Templates', icon: '🎨' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-rose-600 to-pink-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo className="w-12 h-12" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold">Hermod's Path</h1>
                <p className="text-rose-100 text-xs sm:text-sm hidden sm:block">Three platforms. Under five minutes. Zero doom-scrolling.</p>
              </div>
            </div>
            <nav className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={() => navigate('/asgard')}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-rose-700 rounded-lg hover:bg-rose-800 transition text-sm"
              >
                Back to Asgard
              </button>
              <button
                onClick={logout}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 rounded-lg hover:bg-red-700 transition text-sm"
              >
                Logout
              </button>
            </nav>
          </div>

          {/* Top-level tabs */}
          <div className="flex gap-1 mt-4 -mb-4 px-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'drafts') loadDrafts();
                  if (tab.id === 'history') loadHistory();
                }}
                className={`px-4 py-2.5 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'bg-purple-50 text-rose-700'
                    : 'text-rose-200 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="hidden sm:inline">{tab.icon}</span>
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-rose-100 text-rose-700' : 'bg-white/20 text-rose-100'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* ═══ COMPOSE TAB ═══ */}
        {activeTab === 'compose' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Compose Panel — 2/5 width */}
            <div className="md:col-span-1 lg:col-span-2 space-y-5">
              {/* Text Input */}
              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
                <h2 className="text-sm font-bold text-slate-800 mb-2">Raw Content</h2>
                <textarea
                  value={rawText}
                  onChange={handleTextChange}
                  placeholder="What happened today? Dump your bullet points here..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none text-sm"
                  rows={5}
                  style={{ minHeight: '120px', overflow: 'hidden' }}
                />
              </div>

              {/* Photo Upload */}
              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
                <h2 className="text-sm font-bold text-slate-800 mb-2">
                  Photos
                  {images.length > 0 && (
                    <span className="text-xs font-normal text-slate-500 ml-2">{images.length}/10</span>
                  )}
                </h2>
                {images.length < 10 && (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                      dragging
                        ? 'border-rose-500 bg-rose-50'
                        : 'border-gray-300 hover:border-rose-400 hover:bg-rose-50/50'
                    }`}
                  >
                    <ImagePlus size={24} className={`mx-auto mb-1 ${dragging ? 'text-rose-500' : 'text-gray-400'}`} />
                    <p className="text-xs text-gray-600">
                      Drop images or <span className="text-rose-600 font-medium">browse</span>
                    </p>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple onChange={handleFileSelect} className="hidden" />
                {images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                    {images.map((img) => (
                      <div key={img.id} className="relative group aspect-square">
                        <img src={img.preview} alt="" className="w-full h-full object-cover rounded-lg border border-gray-200" />
                        <button
                          onClick={() => removeImage(img.id)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Platform + Content Type — compact row */}
              <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 space-y-3">
                <div>
                  <h2 className="text-sm font-bold text-slate-800 mb-2">Platforms</h2>
                  <div className="flex gap-2">
                    {PLATFORMS.map((platform) => {
                      const isActive = selectedPlatforms.includes(platform.id);
                      return (
                        <div key={platform.id} className="flex-1">
                          <button
                            onClick={() => togglePlatform(platform.id)}
                            className={`w-full py-2 px-2 rounded-lg font-medium text-xs transition-all border-2 ${
                              isActive
                                ? `${platform.activeText} border-transparent shadow-md`
                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                            }`}
                            style={isActive ? { backgroundColor: platform.color } : {}}
                          >
                            {platform.label}
                          </button>
                          {/* Facebook cross-post checkbox — only for IG platforms */}
                          {platform.id === 'professional-ig' && isActive && (
                            <label className="flex items-center gap-1.5 mt-1.5 pl-1 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={includeProfessionalFB}
                                onChange={(e) => setIncludeProfessionalFB(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-[#1877F2] focus:ring-[#1877F2] cursor-pointer"
                              />
                              <span className="text-[11px] text-slate-500 group-hover:text-slate-700 transition">+ Facebook</span>
                            </label>
                          )}
                          {platform.id === 'lux-libris-ig' && isActive && (
                            <label className="flex items-center gap-1.5 mt-1.5 pl-1 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={includeLuxLibrisFB}
                                onChange={(e) => setIncludeLuxLibrisFB(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-[#1877F2] focus:ring-[#1877F2] cursor-pointer"
                              />
                              <span className="text-[11px] text-slate-500 group-hover:text-slate-700 transition">+ Facebook</span>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-800 mb-2">Content Type</h2>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-white text-sm"
                  >
                    {CONTENT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Action buttons */}
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                className={`w-full py-3 px-4 rounded-lg transition font-bold text-base flex items-center justify-center shadow-lg ${
                  canGenerate && !generating
                    ? 'bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {generating ? (
                  <><Loader2 size={18} className="animate-spin mr-2" /> Consulting the Seer...</>
                ) : (
                  <>Ask The Seer {'\u26A1'} <span className="text-xs opacity-60 ml-2 hidden sm:inline">{'\u2318\u21B5'}</span></>
                )}
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveDraft(false)}
                  disabled={saving || !rawText.trim()}
                  className="flex-1 py-2 px-3 rounded-lg font-medium text-xs transition flex items-center justify-center gap-1.5 bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  onClick={handleNewDraft}
                  className="py-2 px-3 rounded-lg font-medium text-xs transition bg-slate-200 text-slate-700 hover:bg-slate-300"
                >
                  New
                </button>
              </div>
              {lastSaved && (
                <p className="text-xs text-slate-400 text-center -mt-3">
                  {hasUnsavedChanges ? 'Unsaved changes' : `Saved ${lastSaved.toLocaleTimeString()}`}
                  {hasUnsavedChanges && rawText.trim() && ' \u2022 auto-saves in 60s'}
                </p>
              )}

              {/* Inspiration Panel */}
              <InspirationPanel
                activeContentType={contentType}
                onSelectIdea={(idea) => {
                  setRawText(idea.exampleBullets);
                  setContentType(idea.contentType);
                  if (idea.suggestedPlatforms?.length) {
                    setSelectedPlatforms(idea.suggestedPlatforms);
                  }
                }}
              />
            </div>

            {/* Preview Panel — 3/5 width */}
            <div className="md:col-span-1 lg:col-span-3 space-y-6">
              {error && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              )}

              {!generatedPosts && !error && (
                <div className="bg-slate-800 rounded-2xl shadow-xl p-12 border border-slate-700 text-center">
                  <div className="text-4xl mb-4">{'\u2728'}</div>
                  <h3 className="text-slate-200 text-lg font-semibold mb-2">Ready to create</h3>
                  <p className="text-slate-400 text-sm max-w-md mx-auto leading-relaxed">
                    Dump your raw notes, bullet points, or ideas in the compose panel.
                    The Seer will craft platform-perfect posts for Professional IG, Lux Libris, and LinkedIn.
                  </p>
                  <div className="flex items-center justify-center gap-6 mt-6 text-xs text-slate-500">
                    <span>{'\u2318\u21B5'} Generate</span>
                    <span>{'\u2318\u21E7'}C Copy all</span>
                  </div>
                </div>
              )}

              {generatedPosts && (
                <>
                  {(generatedPosts.professionalIG || generatedPosts.luxLibrisIG) && (
                    <div className={`grid gap-5 ${generatedPosts.professionalIG && generatedPosts.luxLibrisIG ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
                      {generatedPosts.professionalIG && (
                        <div className="space-y-3">
                          <PreviewCard
                            platformId="professional-ig"
                            icon="📸" label="Professional IG" handle="@drveritykahn" color="#FFD93D"
                            content={getPostText(generatedPosts.professionalIG)} charLimit={2200} copyLabel="Copy Caption"
                            previewImage={images[0]?.preview}
                            imageNote={generatedPosts.professionalIG?.imageNote}
                            imageType={generatedPosts.professionalIG?.imageType}
                            onContentChange={(text) => setGeneratedPosts(prev => ({
                              ...prev, professionalIG: typeof prev.professionalIG === 'string'
                                ? text : { ...prev.professionalIG, text }
                            }))}
                            onCopy={handleCopyLog}
                            onGoToTemplates={() => setActiveTab('templates')}
                            onGenerateImagePrompt={handleGenerateImagePrompt}
                          />
                          {/* Facebook sub-card */}
                          {generatedPosts.professionalFB && (
                            <PreviewCard
                              platformId="professional-fb"
                              icon="📘" label="Professional FB" handle="Dr. Verity Kahn" color="#1877F2"
                              content={getPostText(generatedPosts.professionalFB)} charLimit={2000} copyLabel="Copy Post"
                              imageNote={generatedPosts.professionalFB?.imageNote}
                              imageType={generatedPosts.professionalFB?.imageType}
                              onContentChange={(text) => setGeneratedPosts(prev => ({
                                ...prev, professionalFB: typeof prev.professionalFB === 'string'
                                  ? text : { ...prev.professionalFB, text }
                              }))}
                              onCopy={handleCopyLog}
                              onGenerateImagePrompt={handleGenerateImagePrompt}
                            />
                          )}
                        </div>
                      )}
                      {generatedPosts.luxLibrisIG && (
                        <div className="space-y-3">
                          <PreviewCard
                            platformId="lux-libris-ig"
                            icon="✨" label="Lux Libris IG" handle="@luxlibris" color="#ADD4EA"
                            content={getPostText(generatedPosts.luxLibrisIG)} charLimit={2200} copyLabel="Copy Caption"
                            previewImage={images[0]?.preview}
                            imageNote={generatedPosts.luxLibrisIG?.imageNote}
                            imageType={generatedPosts.luxLibrisIG?.imageType}
                            resolvedAsset={generatedPosts.luxLibrisIG?.imageType === 'lux_asset'
                              ? resolveAssetFromNote(generatedPosts.luxLibrisIG?.imageNote || '')
                              : null}
                            onContentChange={(text) => setGeneratedPosts(prev => ({
                              ...prev, luxLibrisIG: typeof prev.luxLibrisIG === 'string'
                                ? text : { ...prev.luxLibrisIG, text }
                            }))}
                            onCopy={handleCopyLog}
                            onGoToTemplates={() => setActiveTab('templates')}
                            onGenerateImagePrompt={handleGenerateImagePrompt}
                          />
                          {/* Facebook sub-card */}
                          {generatedPosts.luxLibrisFB && (
                            <PreviewCard
                              platformId="lux-libris-fb"
                              icon="📘" label="Lux Libris FB" handle="Lux Libris" color="#1877F2"
                              content={getPostText(generatedPosts.luxLibrisFB)} charLimit={2000} copyLabel="Copy Post"
                              imageNote={generatedPosts.luxLibrisFB?.imageNote}
                              imageType={generatedPosts.luxLibrisFB?.imageType}
                              onContentChange={(text) => setGeneratedPosts(prev => ({
                                ...prev, luxLibrisFB: typeof prev.luxLibrisFB === 'string'
                                  ? text : { ...prev.luxLibrisFB, text }
                              }))}
                              onCopy={handleCopyLog}
                              onGenerateImagePrompt={handleGenerateImagePrompt}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {generatedPosts.linkedin && (
                    <PreviewCard
                      platformId="linkedin"
                      icon="💼" label="LinkedIn" handle="Dr. Verity Kahn, PhD" color="#4389BD"
                      content={getPostText(generatedPosts.linkedin)} charLimit={3000} copyLabel="Copy Post" isWide
                      imageNote={generatedPosts.linkedin?.imageNote}
                      imageType={generatedPosts.linkedin?.imageType}
                      onContentChange={(text) => setGeneratedPosts(prev => ({
                        ...prev, linkedin: typeof prev.linkedin === 'string'
                          ? text : { ...prev.linkedin, text }
                      }))}
                      onCopy={handleCopyLog}
                      onGoToTemplates={() => setActiveTab('templates')}
                      onGenerateImagePrompt={handleGenerateImagePrompt}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ═══ DRAFTS TAB ═══ */}
        {activeTab === 'drafts' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Saved Drafts</h2>
              <button
                onClick={handleNewDraft}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 transition"
              >
                New Draft
              </button>
            </div>

            {drafts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-12 text-center">
                <div className="text-4xl mb-4">{'\uD83D\uDCDD'}</div>
                <h3 className="text-slate-700 text-lg font-semibold mb-2">No drafts yet</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">
                  Your work is automatically saved every 60 seconds, or you can save manually.
                  Drafts live here until you're ready to publish.
                </p>
                <button
                  onClick={handleNewDraft}
                  className="px-6 py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 transition shadow-md"
                >
                  Start Composing
                </button>
              </div>
            ) : (
              drafts.map(draft => (
                <div key={draft.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  <button onClick={() => handleLoadDraft(draft)} className="w-full p-5 text-left hover:bg-slate-50 transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 font-medium line-clamp-2">
                          {draft.rawInput || 'Empty draft'}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{draft.contentType}</span>
                          <span className="text-xs text-slate-400">
                            {draft.updatedAt.toLocaleDateString()} {draft.updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <div className="flex gap-1">
                            {draft.platforms?.map(p => {
                              const plat = PLATFORMS.find(pl => pl.id === p);
                              return plat ? <span key={p} className="w-3 h-3 rounded-full" style={{ backgroundColor: plat.color }} /> : null;
                            })}
                          </div>
                        </div>
                      </div>
                      {(draft.generatedDrafts?.professionalIG || draft.generatedDrafts?.luxLibrisIG || draft.generatedDrafts?.linkedin) && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded shrink-0">Generated</span>
                      )}
                    </div>
                  </button>
                  <div className="border-t border-gray-100 px-5 py-2 flex justify-end">
                    {deleteConfirm === draft.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600">Delete this draft?</span>
                        <button onClick={() => handleDeleteDraft(draft.id)} className="text-xs text-red-600 font-bold hover:text-red-700">Yes</button>
                        <button onClick={() => setDeleteConfirm(null)} className="text-xs text-slate-500 hover:text-slate-700">No</button>
                      </div>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(draft.id); }} className="text-slate-400 hover:text-red-500 transition p-1">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ═══ HISTORY TAB ═══ */}
        {activeTab === 'history' && (
          <div className="max-w-3xl mx-auto space-y-4">
            <h2 className="text-lg font-bold text-slate-800">Post History</h2>

            {/* Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search posts..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                />
              </div>
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-white"
              >
                <option value="">All types</option>
                {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {history.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-12 text-center">
                <div className="text-4xl mb-4">{'\uD83D\uDCCB'}</div>
                <h3 className="text-slate-700 text-lg font-semibold mb-2">No posts published yet</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  Every time you copy a generated post, it's logged here for your records.
                  You'll be able to search and filter your entire posting history.
                </p>
              </div>
            ) : (
              history
                .filter(post => {
                  if (!historySearch) return true;
                  const search = historySearch.toLowerCase();
                  const content = [post.content?.professionalIG, post.content?.professionalFB, post.content?.luxLibrisIG, post.content?.luxLibrisFB, post.content?.linkedin]
                    .filter(Boolean).join(' ').toLowerCase();
                  return content.includes(search) || post.contentType?.toLowerCase().includes(search);
                })
                .map(post => (
                  <div key={post.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {post.copiedAt.toLocaleDateString()} {post.copiedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{post.contentType}</span>
                    </div>
                    <div className="flex gap-1.5 mb-3">
                      {post.platforms?.map(p => {
                        const plat = PLATFORMS.find(pl => pl.id === p);
                        return plat ? (
                          <span key={p} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: plat.color, color: plat.id === 'linkedin' ? '#fff' : '#1e293b' }}>
                            {plat.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                    <div className="space-y-2">
                      {post.content?.professionalIG && (
                        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                          <p className="font-medium text-slate-700 mb-1">Professional IG</p>
                          <p className="line-clamp-3 whitespace-pre-wrap">{post.content.professionalIG}</p>
                        </div>
                      )}
                      {post.content?.professionalFB && (
                        <div className="text-xs text-slate-600 bg-blue-50 rounded-lg p-3 border-l-2 border-[#1877F2]">
                          <p className="font-medium text-slate-700 mb-1">Professional FB</p>
                          <p className="line-clamp-3 whitespace-pre-wrap">{post.content.professionalFB}</p>
                        </div>
                      )}
                      {post.content?.luxLibrisIG && (
                        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                          <p className="font-medium text-slate-700 mb-1">Lux Libris IG</p>
                          <p className="line-clamp-3 whitespace-pre-wrap">{post.content.luxLibrisIG}</p>
                        </div>
                      )}
                      {post.content?.luxLibrisFB && (
                        <div className="text-xs text-slate-600 bg-blue-50 rounded-lg p-3 border-l-2 border-[#1877F2]">
                          <p className="font-medium text-slate-700 mb-1">Lux Libris FB</p>
                          <p className="line-clamp-3 whitespace-pre-wrap">{post.content.luxLibrisFB}</p>
                        </div>
                      )}
                      {post.content?.linkedin && (
                        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                          <p className="font-medium text-slate-700 mb-1">LinkedIn</p>
                          <p className="line-clamp-3 whitespace-pre-wrap">{post.content.linkedin}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>
        )}

        {/* ═══ TEMPLATES TAB ═══ */}
        {activeTab === 'templates' && (
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Image Studio</h2>
                <p className="text-sm text-slate-500">Create branded post graphics</p>
              </div>
            </div>
            <div className="bg-slate-800 rounded-2xl shadow-xl border border-slate-700 p-6">
              <ImageGenerator onAddToPost={handleAddGeneratedImage} />
              <LuxAssetBrowser onSelectAsset={(url, name) => {
                if (images.length >= 10) return;
                const fileName = `${name.replace(/\s+/g, '_').toLowerCase()}.png`;
                const file = new File([], fileName, { type: 'image/png' });
                file.luxAssetUrl = url;
                const img = {
                  id: Date.now() + Math.random(),
                  file,
                  preview: url,
                };
                setImages(prev => [...prev, img].slice(0, 10));
                showToast(`Added ${name} to post`);
              }} />

              {/* AI Prompt Generator — standalone */}
              <div className="mt-6 bg-slate-700/30 border border-slate-600 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 size={14} className="text-amber-400" />
                  <h3 className="text-sm font-bold text-slate-200">Generate with AI</h3>
                </div>
                <p className="text-xs text-slate-400 mb-4">
                  Describe what you want, pick a platform and tool, and get a ready-to-paste prompt for Canva or Leonardo.
                </p>
                <StandalonePromptGenerator />
              </div>
            </div>
          </div>
        )}
      </main>

      <ImagePromptModal
        isOpen={imagePromptModal.isOpen}
        onClose={closeImagePromptModal}
        imageNote={imagePromptModal.imageNote}
        platformId={imagePromptModal.platformId}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default HermodsPath;
